<template>
  <section class="osk-product">
    <div class="osk-product__left">
      <ProductGalleries
        class="osk-product__gallery"
        :product="product"
      />
    </div>

    <div class="osk-product__right">
      <div class="osk-product__buttons-mobile">
        <UikitUserLikesCount
          :is-liked="isLiked"
          :product-id="product.productId!"
          :likes-count="product.likesCount"
          :analytics="{ value: getCommunity(product) }"
          icon-size="l"
          is-hide-zero
          is-reverse
          @like="isLiked = true"
          @dislike="isLiked = false"
        />

        <div
          class="osk-product__buttons-mobile-message"
          @click="showAllComments"
        >
          <UikitIcon
            name="Message"
            size="l"
          />

          <span
            v-if="product.commentsCount"
            class="osk-product__buttons-mobile-message-count"
          >
            {{ product.commentsCount }}
          </span>
        </div>
      </div>

      <div class="osk-product__header">
        <h1 class="osk-product__title">
          <nuxt-link
            v-if="brandLink"
            :to="brandLink"
            class="osk-product__brand"
            @click="handleBrandClick"
          >
            {{ product.brand?.name }}
          </nuxt-link><span
            v-if="modelName"
            class="osk-product_mobile"
          >,&nbsp;</span>

          <span class="osk-product__info">
            <span class="osk-product_desktop osk-product__info-name">
              {{ product.shortSeoDescription ?? product.name }}<span v-if="modelName">
                <span>&nbsp;•&nbsp;</span>
                <span>{{ modelName }}</span>
              </span>
            </span>

            <span
              v-if="modelName"
              class="osk-product_mobile"
            >{{ modelName }}</span>
          </span>

          <span class="osk-product_mobile osk-product__seo">
            {{ product.shortSeoDescription ?? product.name }}
          </span>
        </h1>

        <div class="osk-product__condition">
          {{ product.conditionName }}
        </div>
      </div>

      <div class="osk-product__prices-box">
        <div class="osk-product__prices">
          <div class="osk-product__prices-curr">
            <span
              v-if="product.price"
              class="osk-product__price"
            >
              {{ numberToCurrency(product.price) }}
            </span>

            <UikitIcon
              v-if="discount"
              name="ArrowDown"
              size="m"
              color="red"
              class="osk-product__price-icon"
            />

            <span
              v-if="discount"
              class="osk-product__discount"
            >
              -{{ discount }}%
            </span>
          </div>

          <div class="osk-product__prices-old">
            <span
              v-if="product.startPrice && product.price && product.startPrice > product.price"
              class="osk-product__startPrice"
            >
              {{ numberToCurrency(product.startPrice) }}
            </span>

            <span
              v-if="product.rrpPrice && product.price && product.rrpPrice > product.price"
              class="osk-product__rrpPrice"
            >
              {{ numberToCurrency(product.rrpPrice) }}
            </span>
          </div>
        </div>

        <div
          v-if="product.split"
          class="osk-product__yandex-split"
        >
          <YandexSplitBadge
            :split="product.split"
            size="m"
            show-parts-info
          />

          <UikitIcon
            name="Info"
            @click="onYandexPayInfoClick"
          />
        </div>

        <div
          v-if="product.yandexPlus"
          class="osk-product__yandex-plus"
        >
          <YandexPlusBadge
            :plus="product.yandexPlus"
            size="m"
          />

          <UikitIcon
            name="Info"
            @click="onYandexPayInfoClick"
          />
        </div>
      </div>

      <UikitButton
        v-if="!(isCurrentUserSeller || isSold)"
        class="osk-product__bargaining"
        color="secondary"
        full-width
        @click="openOfferPriceDialog"
      >
        {{ $t('components.product.product.offerYourPrice') }}
      </UikitButton>

      <ProductOCommunitySelectionCard
        v-if="product.exclusiveSelectionTimeForLowStatuses"
        :is-male="product.sex === 'MALE'"
        :date="product.exclusiveSelectionTimeForLowStatuses"
        class="mb-8"
        @banner-link-clicked="sendBannerClickEvent"
      />

      <div
        v-if="isSizes"
        class="osk-product__sizes-select"
      >
        <UikitFormSelect
          v-model="currentSizeId"
          class="osk-product__select"
          name="sizeId"
          :label="$t('components.product.product.currentSize.label')"
          :placeholder="$t('components.product.product.currentSize.placeholder')"
          :disabled="isOnlyOneSize"
        >
          <UikitFormSelectOption
            v-for="item in productRealSizes"
            :key="item.id"
            :label="getSizeOption(item)"
            :value="item.id"
          />
        </UikitFormSelect>

        <div
          v-if="!isOnlyOneSize"
          class="osk-product__block-title osk-product__block-title_mobile"
        >
          {{ $t('components.product.product.isOnlyOneSize') }}
        </div>

        <div
          v-if="isOnlyOneSize && productRealSizes?.length"
          class="osk-product__one-size-mobile"
        >
          <div class="osk-product__block-title">
            {{ $t('components.product.product.isOnlyOneSize') }}
          </div>

          <div>
            <span>
              {{ productRealSizes[0].productSizeType }} {{ productRealSizes[0].productSizeValue }}
            </span>

            <template v-if="productRealSizes[0].productSizeType !== productRealSizes[0].categorySizeType">
              <span class="osk-product__one-size-mobile-separator">|</span>
              <span class="osk-product__one-size-mobile-category">
                {{ productRealSizes[0].categorySizeType }} {{ productRealSizes[0].categorySizeValue }}
              </span>
            </template>
          </div>
        </div>

        <ul
          v-else-if="product?.sizes"
          class="osk-product__sizes"
        >
          <li class="osk-product__sizes-all-first">
            <span class="osk-product__sizes-all-first-product">
              {{ product.sizes[0].productSizeType }}
            </span>

            <span
              v-if="!onlyOneSize"
              class="osk-product__sizes-all-first-category"
            >
              {{ product.sizes[0].categorySizeType }}
            </span>
          </li>

          <li
            ref="$sizesAll"
            class="osk-product__sizes-list"
          >
            <ul class="osk-product__sizes-all">
              <li
                v-for="(item, index) in product.sizes"
                :key="item.id ?? index"
                :class="{
                  'osk-product__sizes-all-item': true,
                  'osk-product__sizes-all-item_not-in-stock': (item.count ?? 0) <= 0,
                  'osk-product__sizes-all-item_active': currentSizeId === item.id,
                }"
                @click="currentSizeId = item.id"
              >
                <span class="osk-product__sizes-all-first-product">
                  {{ item.productSizeValue }}
                </span>

                <span
                  v-if="!onlyOneSize"
                  class="osk-product__sizes-all-first-category"
                >
                  {{ item.categorySizeValue }}
                </span>
              </li>
            </ul>
          </li>
        </ul>

        <div
          class="osk-product__form-sizes"
          @click="openSizeDialog"
        >
          <span>{{ $t('components.product.product.tableSizes') }}</span>
          <UikitIcon
            name="ArrowBack"
            class="osk-product__form-sizes-icon"
          />
        </div>
      </div>

      <ProductActions
        :product="product"
        :is-sizes="isSizes"
        :size-id="currentSizeId"
        :is-liked="isLiked"
        @liked="isLiked = $event"
      />

      <div class="osk-product__about-block">
        <div class="osk-product__block-title">
          {{ $t('components.product.product.aboutProduct') }}
        </div>

        <ProductSeller :product="product" />

        <div
          v-if="isReturnButton"
          class="osk-product__return"
          @click="openReturnDialog"
        >
          <span>{{ $t('components.product.product.howReturnProduct') }}</span>

          <UikitIcon
            name="Info"
            size="m"
            class="osk-menu-bottom-mobile__icon"
          />
        </div>

        <ProductComments
          ref="$comments"
          :product="product"
        />
      </div>

      <div class="osk-product__original">
        <span class="osk-product__original-title">
          {{ $t('components.product.product.originalTitle') }}
        </span>

        <span class="osk-product__original-text">
          {{ $t('components.product.product.originalText') }}
        </span>
      </div>

      <UikitCollapse
        class="osk-product__collapse"
        wide
        @item-click="handleItemClick"
      >
        <UikitCollapseItem
          name="about"
          :title="$t('components.product.product.details')"
        >
          <ProductAttributes :product="product" />
        </UikitCollapseItem>

        <UikitCollapseItem
          name="infoCompany"
          :title="$t('components.product.product.infoCompany')"
        >
          <BusinessAboutService aside />
        </UikitCollapseItem>
      </UikitCollapse>
    </div>

    <ProductSizesInfoDialog
      v-if="isSizes"
      v-model="isShowSizeDialog"
      :size="currentSize"
      :product="product"
      :size-types="sizeTypes"
      @update:size="currentSizeId = $event.id"
    />

    <ProductActionsOfferPriceMobileDialog
      v-if="$device.isMobile"
      v-model="isShowOfferPriceDialog"
    />

    <WebToAppDownloadAppDesktopDialog
      v-else
      v-model="isShowOfferPriceDialog"
      :title="$t('product.product.webToAppDownloadAppDesktopDialog.title')"
    >
      <template #qr-code>
        <img
          src="~assets/img/webToApp/qr_auction.png"
          height="124"
          width="124"
          alt="qr_code"
        >
      </template>
    </WebToAppDownloadAppDesktopDialog>

    <ProductReturnDialog
      v-model="isShowReturnDialog"
      :seller="product.seller"
    />

    <ProductYandexPayInfoDialog
      v-model="isShowYandexPayInfoDialog"
      :split="product.split"
    />
  </section>
</template>

<script lang="ts" setup>
import { ProductDTORes, SizeValueDTORes } from '~/restAPI/data-contracts';
import { getCommunity } from '~/services/analytics/oskellyAnalytics';
import { useProductService } from '~/services/product';
import { useProduct } from '~/providers/product';
import { AnalyticsEvents, AnalyticsPages } from '~/types/analytics';

import { categoryRootById } from '~/components/catalog/consts';

const props = defineProps<{
  product: ProductDTORes,
}>();

const isShowSizeDialog = ref(false);
const isShowOfferPriceDialog = ref(false);
const isShowReturnDialog = ref(false);
const isShowYandexPayInfoDialog = ref(false);
const isLiked = ref(props.product.isLiked);
const $comments = ref();
const $sizesAll = ref();

const { numberToCurrency } = useNumberFormatter();
const { getSizes } = useProductService();
const { SHOW_RETURNS_BANNER_EXPERIMENT } = useExperimentsStore();
const router = useRouter();

const { productRealSizes, isSizes, isOnlyOneSize } = useProduct(toRef(props, 'product'));

const { data: sizeTypes } = useLazyAsyncData(() => {
  if (!props.product.categoryId) return Promise.resolve([]);

  return getSizes(props.product.categoryId);
});

const currentSizeId = ref(getSize());

function getSize() {
  if (isSizes.value && productRealSizes.value?.length === 1) return productRealSizes.value[0].id;

  if (!isSizes.value && productRealSizes.value?.length === 1) return productRealSizes.value[0].id;

  return undefined;
}

const { isAuth, account, openAuthDialog } = useAccountStore();
const { $addEvent } = useNuxtApp();
const { activeMenuCategoryId } = useActiveMenuCategory();

const isCurrentUserSeller = computed<boolean>(() => (isAuth.value ? account.value.id === props.product.seller!.id : false));
const isSold = computed(() => props.product.productState === 'SOLD');

const brandLink = computed(() => {
  const { brand } = props.product;

  if (brand?.urlName && activeMenuCategoryId.value) {
    return `/catalog/${brand.urlName}/${categoryRootById[activeMenuCategoryId.value]}`;
  }

  return '';
});

const modelName = computed(() => props.product.productModel?.name);
const discount = computed(() => (props.product.higherPrice ? props.product.discount ?? 0 : 0));
const currentSize = computed(() => props.product?.sizes?.find((size) => size.id === currentSizeId.value));
const onlyOneSize = computed(() => {
  if (!props.product?.sizes) return false;

  return props.product.sizes[0].productSizeType === props.product.sizes[0].categorySizeType;
});

const isReturnButton = computed(() => {
  const experiment = SHOW_RETURNS_BANNER_EXPERIMENT.value;

  if (!experiment) return false;

  const { seller } = props.product;

  switch (experiment.valueKey) {
    case 'NEVER':
      return false;
    case 'ALWAYS':
      return true;
    case 'ONLY_FOR_PRO_USERS':
      return seller?.isPro;
    case 'ONLY_FOR_SIMPLE_USERS_WITH_RETURNS':
      return seller?.acceptsReturns && !seller.isPro;
    case 'ONLY_FOR_PRO_USERS_AND_SIMPLE_USERS_WITH_RETURNS':
      return seller?.isPro || seller?.acceptsReturns;
    default:
      return false;
  }
});

function formatPrice(price: number) {
  return price?.toLocaleString('ru-RU');
}

function handleItemClick(name: 'infoCompany' | 'about', isActive: boolean) {
  if (!isActive) return;

  const event = name === 'about' ? AnalyticsEvents.ProductAboutClick : AnalyticsEvents.ProductInfoCompanyClick;

  $addEvent(event, { ...props.product });
}

function openSizeDialog() {
  isShowSizeDialog.value = true;

  $addEvent(AnalyticsEvents.ProductSizeTableOpen, { ...props.product });
}

function getSizeOption(item: SizeValueDTORes) {
  if (item.productSizeType === item.categorySizeType) {
    return `${item.productSizeValue} ${item.productSizeType}`;
  }

  return `${item.productSizeValue} ${item.productSizeType} (${item.categorySizeType} ${item.categorySizeValue})`;
}

function openOfferPriceDialog() {
  isShowOfferPriceDialog.value = true;

  $addEvent(AnalyticsEvents.ProductBargain, { ...props.product });
}

function openReturnDialog() {
  isShowReturnDialog.value = true;
}

function handleBrandClick() {
  $addEvent(AnalyticsEvents.BrandClick, { ...props.product });
}

function showAllComments() {
  if (!isAuth.value) {
    openAuthDialog({ chapter: 'product_comments' });
    return;
  }

  if (!$comments.value) return;

  $comments.value.showAllComments('all');
}

function onYandexPayInfoClick() {
  $addEvent(AnalyticsEvents.YandexSplitInfo, {
    productId: props.product.productId,
    page: AnalyticsPages.ProductPage,
  });

  isShowYandexPayInfoDialog.value = true;
}

function sendBannerClickEvent() {
  $addEvent(AnalyticsEvents.ExclusiveSelectionDetailsClick, {
    page: AnalyticsPages.ProductPage,
    product_id: props.product.productId,
    value: getCommunity(props.product),
  });
}

onMounted(() => {
  if (!$sizesAll.value) return;

  const activeEl = $sizesAll.value.querySelector('.osk-product__sizes-all-item:not(.osk-product__sizes-all-item_not-in-stock)');
  const offsetLeft = activeEl?.offsetLeft;

  if (activeEl && offsetLeft) {
    $sizesAll.value.scrollLeft = offsetLeft - 41;
  }
});
</script>

<style lang="scss" scoped>
@import "~/assets/scss/settings/index";

.osk-product {
  margin-bottom: 24px;

  @include media-query(lg-and-up) {
    display: flex;
    justify-content: space-between;
    margin-bottom: 60px;
  }

  &_mobile {
    display: inline-block;

    @include media-query(lg-and-up) {
      display: none;
    }
  }

  &_desktop {
    display: none;
    @include media-query(lg-and-up) {
      display: inline-block;
    }
  }

  &__left {
    @include media-query(lg-and-up) {
      width: calc(100% - 393px);
      padding-right: 80px;
      position: relative;
      display: flex;
    }
  }

  &__gallery {
    @include media-query(lg-and-up) {
      position: sticky;
      margin-bottom: -12px;
      width: 100%;
      bottom: 8px;
      align-self: flex-end;

      @media screen and (min-height: 845px) {
        top: 216px;
        bottom: auto;
        align-self: flex-start;
      }
    }
  }

  &__right {
    position: relative;
    z-index: 2;
    padding-top: 22px;

    @include media-query(lg-and-up) {
      width: 393px;
      flex-shrink: 0;
      padding-top: 0;
    }
  }

  &__header {
    @include media-query(lg-and-up) {
      display: flex;
      flex-direction: column-reverse;
      margin-top: 0;
    }
  }

  &__title {
    @include font-style($font-size-heading, $line-height-heading, $font-weight-bold);
    display: flex;
    flex-wrap: wrap;
    margin: 0;
    margin-bottom: $indent-xsmall;

    @include media-query(lg-and-up) {
      margin-bottom: 0;
    }
  }

  &__condition {
    color: $grey;

    @include media-query(lg-and-up) {
      @include font-style($font-size-subheading, $line-height-subheading, $font-weight-medium);
      margin-bottom: $indent-xsmall;
    }
  }

  &__brand {
    @include media-query(lg-and-up) {
      @include font-style($font-size-title, $line-height-title, $font-weight-bold);
      margin-bottom: $indent-xsmall;
    }

    &:hover {
      text-decoration: underline;
    }
  }

  &__info {
    display: flex;

    @include media-query(lg-and-up) {
      width: 100%;
      @include font-style($font-size-subheading, $line-height-subheading, $font-weight-medium);
    }
  }

  &__info-name {
    margin-right: 6px;

    @include media-query(lg-and-up) {
      margin-right: 0;
    }
  }

  &__seo {
    width: 100%;
    @include font-style($font-size-body, 19px, $font-weight-medium);
    margin-top: 4px;
  }

  &__yandex-plus,
  &__yandex-split {
    display: flex;
    align-items: center;
    margin-top: 8px;

    .osk-yandex-split-badge {
      margin-right: 4px;
    }

    .osk-yandex-plus-badge {
      margin-right: 4px;
    }
  }

  &__prices-box {
    margin-top: 18px;
    margin-bottom: 28px;

    @include media-query(lg-and-up) {
      margin-top: 32px;
      margin-bottom: $indent-medium;
    }

  }

  &__prices {
    @include media-query(lg-and-up) {
      max-width: 100%;
      display: flex;
      flex-direction: column-reverse;
    }
  }

  &__bargaining {
    margin-bottom: 34px;

    @include media-query(lg-and-up) {
      margin-bottom: 42px;
    }
  }

  &__prices-curr {
    display: flex;
    align-items: center;
    margin-bottom: $indent-xsmall;

    @include media-query(lg-and-up) {
      margin-bottom: 0;
      margin-top: $indent-xsmall;
    }
  }

  &__price {
    @include font-style($font-size-heading, $line-height-heading, $font-weight-bold);
  }

  &__price-icon {
    margin-left: $indent-xsmall;

    @include media-query(lg-and-up) {
      width: 16px !important;
      height: 16px !important;
    }
  }

  &__discount {
    margin-left: auto;

    @include media-query(lg-and-up) {
      @include font-style($font-size-heading, $line-height-heading, $font-weight-medium);
      margin-left: 8px;
    }
  }

  &__prices-old {
    display: flex;
  }

  &__startPrice {
    text-decoration: line-through;
    color: $red;
    margin-right: $indent-small;

    @include media-query(lg-and-up) {
      @include font-style($font-size-subheading, $line-height-subheading, $font-weight-medium);
    }
  }

  &__rrpPrice {
    text-decoration: line-through;
    color: $grey;

    @include media-query(lg-and-up) {
      @include font-style($font-size-subheading, $line-height-subheading, $font-weight-medium);
    }
  }

  &__block {
    margin-bottom: $indent-mlarge;
  }

  &__block-title,
  :deep(.osk-field__label) {
    @include font-style($font-size-heading, $line-height-heading, $font-weight-bold);
    margin-bottom: $indent-small;
    color: $black;
  }

  &__block-title {
    &_mobile {
      @include media-query(lg-and-up) {
        display: none;
      }
    }
  }

  &__sizes-select {
    position: relative;
  }

  &__form-sizes {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 16px;
    margin-top: 8px;

    @include media-query(lg-and-up) {
      position: absolute;
      top: 2px;
      right: 0;
      text-decoration: underline;
      font-weight: $font-weight-semibold;
      color: $grey;
      padding: 0;
      margin-top: 0;
    }

    &:hover {
      text-decoration: none;
    }
  }

  &__form-sizes-icon {
    transform: rotate(180deg);

    @include media-query(lg-and-up) {
      display: none;
    }
  }

  &__select {
    display: none;

    @include media-query(lg-and-up) {
      display: block;
    }
  }

  &__sizes {
    display: flex;
    align-items: center;
    margin-top: 18px;

    @include media-query(lg-and-up) {
      display: none;
    }
  }

  &__sizes-all {
    width: 100%;
    display: flex;
    align-items: center;
  }

  &__sizes-all-first {
    display: flex;
    flex-direction: column;
    margin-right: 14px;
    flex-shrink: 0;
  }

  &__sizes-list {
    width: 100%;
    overflow-x: auto;
    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__sizes-all-first-category {
    color: $grey;
    margin-top: 2px;
  }

  &__sizes-all-item {
    width: 52px;
    height: 52px;
    border: 0.5px solid $grey-2;
    border-radius: 2px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    &:not(:last-child) {
      margin-right: $indent-small;
    }

    &_not-in-stock {
      cursor: default;
      pointer-events: none;

      .osk-product__sizes-all-first-product,
      .osk-product__sizes-all-first-category {
        opacity: 0.3;
      }
    }

    &_active {
      background: $black;
      color: $white;

      .osk-product__sizes-all-first-category {
        color: $white;
      }
    }
  }

  &__buttons-mobile {
    display: flex;
    align-items: center;
    position: absolute;
    top: -20px;

    @include media-query(lg-and-up) {
      display: none;
    }
  }

  &__buttons-mobile-message {
    margin-left: 16px;
  }

  &__buttons-mobile-message-count {
    margin-left: 5px;
  }

  &__one-size-mobile {
    display: flex;
    justify-content: space-between;
    align-items: center;

    @include media-query(lg-and-up) {
      display: none;
    }

    .osk-product__block-title {
      margin-bottom: 0;
    }
  }

  &__one-size-mobile-separator {
    margin: 0 4px;
  }

  &__one-size-mobile-category {
    color: $grey;
  }

  &__collapse {
    margin-top: 44px;
  }

  &__about-block {
    margin-top: 32px;

    @include media-query(lg-and-up) {
      margin-top: 52px;
    }

    .osk-product__block-title {
      margin-bottom: 24px;
    }
  }

  &__original {
    width: 100%;
    max-width: 393px;
    height: 98px;
    background: url(~/assets/img/original.jpeg) no-repeat center center;
    background-size: cover;
    margin-bottom: 32px;
    margin-top: 28px;
    color: #fff;
    display: flex;
    flex-direction: column;
    padding-top: 20px;
    padding-left: 16px;

    @include media-query(lg-and-up) {
      height: 102px;
      margin-top: 32px;
      max-width: 100%;
    }
  }

  &__original-title {
    @include font-style($font-size-subheading, $line-height-subheading, $font-weight-bold);
    margin-bottom: 8px;
  }

  &__original-text {
    @include font-style($font-size-caption, $line-height-caption, $font-weight-medium);
    width: 252px;

    @include media-query(lg-and-up) {
      @include font-style($font-size-body, $line-height-body, $font-weight-medium);
      width: 261px;
    }
  }

  :deep(.osk-product-seller) {
    margin-bottom: $indent-large;
  }

  &__return {
    background: $grey-4;
    border-radius: 2px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10.5px 16px;
    margin-bottom: 24px;
    cursor: pointer;

    @include media-query(lg-and-up) {
      padding: 12px 12.5px 12px 18px;
      transition: all linear .2s;

      &:hover {
        opacity: 0.8;
      }

      &:active {
        opacity: 0.6;
      }
    }
  }
}
</style>
